import Splide from "@splidejs/splide";
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// ScrollTriggerプラグインを登録
gsap.registerPlugin(ScrollTrigger);

export function initTopPageEffects() {
  const aboutImg = document.querySelector<HTMLElement>(
    '[data="top-about-image"]'
  );
  const serviceSection = document.querySelector<HTMLElement>(
    '[data="top-service"]'
  );
  const worksSection = document.querySelector<HTMLElement>(
    '[data="top-works-section"]'
  );
  if (!aboutImg || !serviceSection || !worksSection) return;

  const aboutMv = document.querySelector<HTMLElement>('[data="top-about-mv"]');
  const aboutMvImage = document.querySelector<HTMLElement>(
    '[data="top-about-mv-image"]'
  );
  const aboutScreen = document.querySelector<HTMLElement>(
    '[data="top-about-screen"]'
  );
  const aboutSection = document.querySelector<HTMLElement>(
    '[data="top-about-section"]'
  );

  // 初期状態
  gsap.set(aboutImg, { autoAlpha: 1 });

  const isMobileViewport = () =>
    window.matchMedia("(max-width: 768px)").matches;

  // SPメニュー開閉判定（.header-sp-container に .is-sp_menu_active が付与されているか）
  const isSpMenuActive = () =>
    document.querySelector(".header-sp-container.is-sp_menu_active") !== null;

  // // モバイルかつ SPメニュー未開時のみ動作
  const shouldRunMobile = () => isMobileViewport() && !isSpMenuActive();

  document.addEventListener("spMenuToggle", () => {
    ScrollTrigger.refresh();
  });

  ScrollTrigger.create({
    trigger: serviceSection,
    start: "bottom bottom", // service の底部 が viewport 底
    end: "bottom bottom", // 単一点 (瞬間)
    onEnter: () => {
      if (isMobileViewport()) return;
      // 下方向: 非表示 (即時)
      gsap.set(aboutImg, { autoAlpha: 0, duration: 0.1 });
      gsap.set(aboutMv, { autoAlpha: 0, duration: 0.1 });
    },
    onLeaveBack: () => {
      if (isMobileViewport()) return;
      // 上方向: 再表示 (即時)
      gsap.set(aboutImg, { autoAlpha: 1, duration: 0.1 });
      gsap.set(aboutMv, { autoAlpha: 1, duration: 0.1 });
    },
  });

  if (aboutMv && aboutScreen && aboutSection) {
    // 初期状態をリセット
    gsap.set(aboutMv, {
      clearProps: "all",
    });

    // aboutMvを画面に固定し、スクロールに影響されないようにする
    gsap.set(aboutMv, {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      transformOrigin: "center center",
      x: 0,
      y: 0,
      scale: 1.05,
      rotation: 0,
      zIndex: 1,
    });

    // ScrollTriggerを作成
    ScrollTrigger.create({
      trigger: aboutSection,
      start: "top bottom", // aboutセクションの上部が画面下に現れた時
      end: "center center", // aboutセクションの中央が画面中央に到達した時
      scrub: true, // スクロールに連動（数値ではなくtrueに変更）
      onUpdate: (self) => {
        if (isMobileViewport() && isSpMenuActive()) return; // SPメニュー開時は更新停止

        // const shouldRunMobile = () => isMobileViewport() && !isSpMenuActive();
        // if (!shouldRunMobile()) return;

        const progress = self.progress;

        // aboutScreenの位置を取得（固定位置）
        const screenRect = aboutScreen.getBoundingClientRect();

        // 初期状態：画面全体 (100vw x 100vh) の中央
        const initialCenterX = window.innerWidth / 2;
        const initialCenterY = window.innerHeight / 2;

        // 目標位置：aboutScreenの中央
        const targetCenterX = screenRect.left + screenRect.width / 2;
        const targetCenterY = screenRect.top + screenRect.height / 2;

        // 移動量計算
        const translateX = (targetCenterX - initialCenterX) * progress;
        const translateY = (targetCenterY - initialCenterY) * progress;

        // aboutScreenの幅に合わせたスケール計算
        // 初期サイズ：100vw x 100vh
        const initialWidth = window.innerWidth;

        const AMOUNT_TO_SHRINK = 0.75; // 最終的に75%のサイズに縮小する
        // 終点でaboutScreenと同じ幅になるように計算
        const targetScale =
          (screenRect.width / initialWidth) *
          (AMOUNT_TO_SHRINK - (isMobileViewport() ? 0.12 : 0));

        // スクロール進行に応じてスケールを適用（1 → targetScale）
        const scale = 1.05 + (targetScale - 1) * progress;

        function getAspectSign(threshold: number = 0) {
          const w = window.innerWidth;
          const h = window.innerHeight;
          // threshold > 0 なら正方形付近を判定緩和
          if (Math.abs(w - h) <= threshold) {
            return w >= h ? 1 : -1;
          }
          return w > h ? 1 : -1;
        }

        // 回転
        // const rotation = -45 * getAspectSign() * progress;
        // const rotation = -45 * getAspectSign() * progress;
        const ROTATE_START = 0.08; // 回転開始しきい値
        const rotateProgress =
          progress <= ROTATE_START
            ? 0
            : (progress - ROTATE_START) / (1 - ROTATE_START); // 0.1以降で0→1
        const rotation = -45 * getAspectSign() * rotateProgress;

        // 変形適用
        gsap.set(aboutMv, {
          x: translateX,
          y: translateY,
          scale: scale, // scaleX, scaleYではなくscaleを使用
          rotation: rotation,
          force3D: true,
        });

        // aboutMvImageのopacityをスクロール進行に応じて0に向かって変化
        if (aboutMvImage) {
          const opacity = 1 - progress; // 進行度が1に近づくほど透明に
          gsap.set(aboutMvImage, {
            opacity: opacity,
          });
        }
      },

      //
      onLeave: () => {
        if (!shouldRunMobile()) return;
        gsap.killTweensOf(aboutImg);
        gsap.killTweensOf(aboutMv);
        gsap.to(aboutImg, { autoAlpha: 0, duration: 0.2 }).then(() => {
          gsap.set(aboutImg, { display: "none" });
        });
        gsap.to(aboutMv, { autoAlpha: 0, duration: 0.2 }).then(() => {
          gsap.set(aboutMv, { display: "none" });
        });
      },
      // 巻き戻し（上方向に再度範囲へ入った瞬間）
      onEnterBack: () => {
        if (!shouldRunMobile()) return;
        gsap.killTweensOf(aboutImg);
        gsap.killTweensOf(aboutMv);
        gsap.set(aboutImg, { display: "block" });
        gsap.set(aboutMv, { display: "block" });
        gsap.to(aboutImg, { autoAlpha: 1, duration: 0.1 });
        gsap.to(aboutMv, { autoAlpha: 1, duration: 0.1 });
      },
      //
      onEnter: () => {
        if (isMobileViewport() || isSpMenuActive()) return;
        // 下方向: 非表示 (即時)
        gsap.killTweensOf(aboutImg);
        gsap.killTweensOf(aboutMv);
        gsap.set(aboutImg, { autoAlpha: 1, duration: 0.1 });
        gsap.set(aboutMv, { autoAlpha: 1, duration: 0.1 });
      },
      onLeaveBack: () => {
        if (isMobileViewport() || isSpMenuActive()) return;
        // 下方向: 非表示 (即時)
        gsap.killTweensOf(aboutImg);
        gsap.killTweensOf(aboutMv);
        gsap.set(aboutImg, { autoAlpha: 1, duration: 0.1 });
        gsap.set(aboutMv, { autoAlpha: 1, duration: 0.1 });
      },
    });
  }

  // ----------------------------------------------
  // 追加: endTrigger を使い aboutSection から worksSection までを単一区間として扱い、
  // その終端到達(onLeave)時も既存の非表示処理を適用する。
  if (aboutSection && worksSection && aboutImg && aboutMv) {
    const hideAbout = () => {
      gsap.set([aboutImg, aboutMv], { autoAlpha: 0, duration: 0.01 });
    };
    ScrollTrigger.create({
      trigger: aboutSection,
      start: "top bottom", // 既存定義と揃える
      endTrigger: worksSection,
      end: "top bottom", // worksSection の上端が viewport 中央に来たら終端
      onLeave: () => {
        hideAbout();
      },
    });
  }

  const splideInit = () => {
    let splide_news: Splide;
    const remToPx = (rem: number) =>
      rem * parseFloat(getComputedStyle(document.documentElement).fontSize);

    // News Splide
    const newsSplideElement = document.querySelector(".news-splide");
    if (newsSplideElement) {
      splide_news = new Splide(".news-splide", {
        type: "slide",
        start: 0,
        gap: "2rem",
        trimSpace: true,
        classes: {
          arrows: "splide__arrows news-splide-arrows",
          arrow: "splide__arrow news-splide-arrow",
          prev: "splide__arrow--prev news-splide-arrow-prev",
          next: "splide__arrow--next news-splide-arrow-next",
        },
        autoWidth: false, // 明示的に false
        pagination: false,
        arrows: true,
        fixedWidth: "21rem", // CSS と同じ値
        perMove: 1,
        drag: true,
        snap: true,
        focus: 0, // 左揃え
        isNavigation: false, // ナビゲーション要素ではないことを明示
        slideFocus: true, // スライド要素自体のフォーカス管理を有効化
        mediaQuery: "max",
        breakpoints: {
          [remToPx(64)]: {
            perPage: 1,
            gap: "1rem",
            fixedWidth: remToPx(20) + "px", // CSS変数ではなく固定値
            focus: "center",
          },
        },
      });

      splide_news.mount();
    }

    // Client Splide - オートスクロール
    const clientSelectors = [
      ".client-splide-1",
      ".client-splide-2",
      ".client-splide-3",
    ];

    clientSelectors.forEach((selector, index) => {
      const element = document.querySelector(selector);
      if (!element) return;

      // スライドを複製
      const slidesList = element.querySelector(".splide__list");
      const originalSlides = slidesList?.querySelectorAll(".splide__slide");

      if (slidesList && originalSlides && originalSlides.length > 0) {
        for (let i = 0; i < 2; i++) {
          originalSlides.forEach((slide) => {
            slidesList.appendChild(slide.cloneNode(true));
          });
        }
      }

      // 2列目だけ逆方向
      const speed = index === 1 ? -0.3 : 0.3;

      const splide = new Splide(selector, {
        type: "loop",
        drag: false,
        arrows: false,
        pagination: false,
        autoWidth: true,
        gap: "1rem",
        autoScroll: {
          speed: speed,
          pauseOnHover: false,
        },
      });

      splide.mount({ AutoScroll });
    });

    // interview Splide
    let splide_interview: Splide;
    const interviewSplideElement = document.querySelector(".interview-splide");
    if (interviewSplideElement) {
      splide_interview = new Splide(".interview-splide", {
        type: "slide",
        start: 0,
        gap: "2rem",
        trimSpace: true,
        classes: {
          arrows: "splide__arrows interview-splide-arrows",
          arrow: "splide__arrow interview-splide-arrow",
          prev: "splide__arrow--prev interview-splide-arrow-prev",
          next: "splide__arrow--next interview-splide-arrow-next",
        },
        autoWidth: false, // 明示的に false
        pagination: false,
        arrows: true,
        fixedWidth: "21rem", // CSS と同じ値
        perMove: 1,
        drag: true,
        snap: true,
        focus: 0, // 左揃え
        isNavigation: false, // ナビゲーション要素ではないことを明示
        slideFocus: true, // スライド要素自体のフォーカス管理を有効化
        mediaQuery: "max",
        breakpoints: {
          [remToPx(64)]: {
            perPage: 1,
            gap: "1rem",
            fixedWidth: remToPx(20) + "px", // CSS変数ではなく固定値
            focus: "center",
          },
        },
      });

      splide_interview.mount();
    }

    // 情報揃うまで一旦未公開
    // partner Splide - オートスクロール
    // const partnerSelectors = [
    //   ".partner-splide-1",
    //   ".partner-splide-2",
    //   ".partner-splide-3",
    // ];

    // partnerSelectors.forEach((selector, index) => {
    //   const element = document.querySelector(selector);
    //   if (!element) return;

    //   // スライドを複製
    //   const slidesList = element.querySelector(".splide__list");
    //   const originalSlides = slidesList?.querySelectorAll(".splide__slide");

    //   if (slidesList && originalSlides && originalSlides.length > 0) {
    //     for (let i = 0; i < 16; i++) {
    //       originalSlides.forEach((slide) => {
    //         slidesList.appendChild(slide.cloneNode(true));
    //       });
    //     }
    //   }

    //   // 2列目だけ逆方向
    //   const speed = index === 1 ? -0.3 : 0.3;

    //   const splide = new Splide(selector, {
    //     type: "loop",
    //     drag: false,
    //     arrows: false,
    //     pagination: false,
    //     autoWidth: true,
    //     gap: "1rem",
    //     autoScroll: {
    //       speed: speed,
    //       pauseOnHover: false,
    //     },
    //   });

    //   splide.mount({ AutoScroll });
    // });
  };
  splideInit();
}
